import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { App, Spin } from "antd";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  loginWithCode,
  fetchUserInfo,
  showLicenseBlocked,
  switchToNonPersonal,
} from "../store/authSlice";
import { isLicenseBlockedDetail } from "../lib/licenseNotice";
import {
  EMPLOYEE_KEY,
  PING_KEY,
  TOKEN_KEY,
  clearEmployeeRecordIf,
  getAccountIsPersonal,
  getTerminalToken,
  parseEmployeeRecord,
} from "../lib/auth";
import {
  answerPing,
  endSessionForExpiredLicense,
  fullLogout,
  isLoggingOut,
  markContextWarm,
  probeTerminalToken,
} from "../lib/sessionRecovery";
import { getRestaurantName } from "../lib/restaurant";
import { useIsEdgeRestricted } from "../hooks/useEdgeMode";
import { useEscBack } from "../components/profileActions";
import CodeKeypad from "../pages/ordering/CodeKeypad";
import { useEmployeeSessionRestore } from "./useEmployeeSessionRestore";
import { useAutoCodeLogout } from "./useAutoCodeLogout";
// Esc не навигира в поле за писане и при отворен antd слой — проверките са
// общи с клавиатурата на работните листове.
import { hasOpenOverlay, isEditableTarget } from "../lib/overlay";

/**
 * Глобална пин-бариера за акаунт за ОБЩ ДОСТЪП (`account_is_personal === false`).
 *
 * Логика (терминалът е „общ" когато `account_is_personal === false` в localStorage):
 * - Личен терминал (`true`) или неизвестно (`null`) → пуска децата директно, без
 *   пин (свободният логин остава непроменен).
 * - Общ терминал с влязъл служител (`state.auth.personal === true`) → пуска децата.
 *   Това е същият сигнал, който ползват вторичните logout бутони.
 * - Общ терминал без влязъл служител (`personal !== true`) → показва клавиатурата
 *   за код. След вярна проверка зарежда ролите на служителя (`fetchUserInfo`) и
 *   едва тогава пуска децата.
 *
 * NB: при първоначалния вход със споделения акаунт Redux вече съдържа токена X,
 * затова за „влязъл служител" разчитаме на `personal`, а не на наличие на токен.
 *
 * КЪДЕ ЖИВЕЕ СЛУЖИТЕЛЯТ: в Redux (за текущия екран) и в localStorage под
 * ключа `employee` — ОБЩ за всички табове и прозорци на устройството.
 * Бекендът пази ЕДНА сесия за терминала, затова два прозореца с двама различни
 * служители са невъзможно състояние: влезлият последен тихо убива токена на
 * другия. Преди записът беше в sessionStorage (отделен за всеки таб) и точно
 * това разминаване чупеше терминала.
 *
 * КОГА ИСКА ПИН:
 *  • refresh на вече работил прозорец → НЕ (маркерът `ctx` в sessionStorage);
 *  • нов таб/прозорец до жив съсед    → НЕ (съседът отговаря на ping);
 *  • студен старт (изключена и пусната наново каса) → ДА, както досега;
 *  • друг прозорец е излязъл или е влязъл друг служител → ДА (storage event).
 *
 * Лицензът остава един: `loginWithCode` подменя session_token на същата
 * (споделена) сесия. Невалиден запазен токен сваля САМО служителя до ПИН —
 * решението „до ПИН или пълен изход" е изцяло в lib/sessionRecovery.ts.
 */
export default function SharedAccountGate({
  children,
}: {
  children: ReactNode;
}) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const isPersonal = getAccountIsPersonal();
  // Ограничен локален режим (§14.3.1, docs/edge.md): срещу касата сме без
  // билет — през нея минава само ПИН входът, който минтва ЛОКАЛЕН билет.
  // Затова служителят от кеша (или от преди прекъсването) пак минава през
  // клавиатурата: без билет нито една заявка не би стигнала до касата.
  const edgeRestricted = useIsEdgeRestricted();

  // Възстановяване на служителя след refresh и при смяна на режима облак/каса
  // — спинър, докато токенът се потвърди (кеширана самоличност → веднага).
  // Невалиден токен само сваля до ПИН екрана — НЕ минава през 401
  // интерсептора, който би изхвърлил цялото устройство към /login.
  const restoring = useEmployeeSessionRestore();
  // Топлият маркер се слага ЕДВА когато прозорецът реално носи жив служител
  // (успешен ПИН вход, успешно възстановяване, осиновяване). Слагането му при
  // монтиране маркираше „топъл" и прозорец, паднал до ПИН при студен старт —
  // и първият F5 възкресяваше снощния служител без код.
  useEffect(() => {
    if (isPersonal === false && currentUserPersonal === true) markContextWarm();
  }, [isPersonal, currentUserPersonal]);

  // Синхронизация МЕЖДУ прозорците на терминала. `storage` се стреля само в
  // ДРУГИТЕ контексти на същия origin — точно каквото трябва (и защитата срещу
  // примка: записващият не се самоуведомява).
  const personalRef = useRef(currentUserPersonal);
  personalRef.current = currentUserPersonal;
  const reduxToken = useAppSelector((s) => s.auth.token);
  const tokenRef = useRef(reduxToken);
  tokenRef.current = reduxToken;

  useEffect(() => {
    if (isPersonal !== false) return;
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return;
      if (isLoggingOut()) return;

      // Нов прозорец пита „има ли тук жив служител". Отговаряме само ако наистина
      // има — иначе новият прозорец правилно ще поиска ПИН.
      if (e.key === PING_KEY && e.newValue) {
        if (personalRef.current === true) answerPing(e.newValue);
        return;
      }

      // Идентичността на самия терминал се е сменила.
      if (e.key === TOKEN_KEY) {
        if (!e.newValue) {
          // Друг прозорец е излязъл напълно от устройството.
          if (window.location.pathname !== "/login")
            window.location.href = "/login";
        } else if (e.oldValue && e.newValue !== e.oldValue) {
          // Влизано е наново с потребител+парола — целият контекст е стар.
          window.location.href = "/";
        }
        return;
      }

      // Служителят на терминала се е сменил (или си е тръгнал).
      if (e.key === EMPLOYEE_KEY) {
        const theirEmployee = parseEmployeeRecord(e.newValue)?.t ?? null;
        if (theirEmployee === tokenRef.current) return; // нищо ново за нас
        if (!getTerminalToken()) {
          // Триенето е част от пълен изход (виж clearToken) — токенът вече го
          // няма и следващото събитие само би повторило същата навигация.
          if (window.location.pathname !== "/login")
            window.location.href = "/login";
          return;
        }
        // Пълна навигация: Redux, react-query кешът и отворените сокети са на
        // СТАРИЯ служител и не бива да оцелеят смяната.
        window.location.href = "/";
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [isPersonal]);

  // Глобална Esc-навигация „назад": общ терминал → модул → „Модули" (при >1
  // модула на служителя) → изход от тестовия режим (при access=true)
  // → ПИН екран; личен акаунт → само модул → „Модули",
  // БЕЗ изход (escBack е null, когато Esc няма какво да направи — тогава
  // нищо не се закача). Проверката е ОТЛОЖЕНА със setTimeout, за да минат
  // първо ВСИЧКИ други Esc handlers — Esc, консумиран от тях (preventDefault)
  // или паднал върху отворен antd слой, не навигира. Редът на window
  // listeners не е гарантиран, затова не разчитаме на него, а на
  // състоянието СЛЕД като всички са минали.
  const escBack = useEscBack();
  useEffect(() => {
    if (!escBack) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isEditableTarget(e.target)) return;
      window.setTimeout(() => {
        if (e.defaultPrevented) return;
        if (hasOpenOverlay()) return;
        escBack();
      }, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [escBack]);

  // „Автоматичен изход до код" — само докато служител, влязъл с код, реално
  // работи (същото условие като пускането на децата по-долу).
  useAutoCodeLogout(
    isPersonal === false &&
      !restoring &&
      currentUserPersonal === true &&
      !loading &&
      !edgeRestricted,
  );

  // Личен терминал (или неопределен) — без пин
  if (isPersonal !== false) return <>{children}</>;

  // Тече възстановяване на служителя (refresh, смяна на режима) — нито ПИН
  // екран (би мигнал и приел вход), нито децата (ролите още ги няма).
  if (restoring)
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );

  // Общ терминал с вече влязъл служител (личен потребител през код) — пуска децата.
  // Докато тече проверката на кода (`loading`), задържаме клавиатурата, за да не
  // мигне UI с още незаредените роли (loginWithCode → fetchUserInfo не са атомарни).
  // В ограничен локален режим децата не се пускат при никакви обстоятелства.
  if (currentUserPersonal === true && !loading && !edgeRestricted)
    return <>{children}</>;

  const handleCodeSubmit = async (code: string) => {
    setLoading(true);
    // Токенът, издаден на този вход — ако валидацията му се провали, трябва да
    // изчистим ТОЧНО него от общия запис (compare-and-clear), за да не осиновят
    // съседните прозорци непотвърден служител.
    let issuedToken: string | null = null;
    try {
      const restaurant_name = getRestaurantName();
      const shared_account_token = getTerminalToken();

      const result = await dispatch(
        loginWithCode({
          restaurant_name,
          code,
          shared_account_token: shared_account_token || undefined,
        }),
      ).unwrap();
      issuedToken = result.access_token;

      // Зареждаме ролите/правата на служителя, влязъл с код — модулите и
      // route guard-овете се базират на тях. `skipAuthRedirect`, защото провал
      // тук се обработва по-долу, а не от глобалния 401 интерсептор.
      await dispatch(fetchUserInfo({ skipAuthRedirect: true })).unwrap();

      queryClient.invalidateQueries({ queryKey: ["menu"] });
      message.success(t("codeKeypad.codeVerified"));
    } catch (error) {
      if (issuedToken) {
        clearEmployeeRecordIf(issuedToken);
        dispatch(switchToNonPersonal());
      }
      let errorMessage = t("codeKeypad.invalidCode");

      if (typeof error === "string") {
        errorMessage = error;
      } else if (Array.isArray(error) && error.length > 0) {
        errorMessage = error[0].msg || errorMessage;
      } else if (isAxiosError(error)) {
        errorMessage = error.response?.data?.detail || errorMessage;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String(error.message);
      }

      // Гратисният период на лиценза е минал (403 `license_expired`) за ТОЗИ
      // служител. Терминал без „Справки"/„Склад" също е мъртъв — пълен изход
      // към /login с модала „Достъпът е спрян" (и от /monitors). Терминал в
      // режим „само преглед" (lib/licenseReadOnly.ts) е жив: модалът и ПИН
      // екранът остават за служител, който може да влезе. Пробата решава —
      // пълен изход само при доказано мъртъв X (както в sessionRecovery.ts).
      if (isLicenseBlockedDetail(error)) {
        if ((await probeTerminalToken()) === "dead") endSessionForExpiredLicense(error);
        else dispatch(showLicenseBlocked(error));
        return;
      }
      message.error(errorMessage);

      // Ако причината е, че сесията на самия ТЕРМИНАЛ е угаснала (изгонен от
      // друго устройство, изтекъл X), ПИН екранът е задънена улица — никакъв
      // код няма да проработи. Пробата е авторитетът; текстът на грешката не се
      // сравнява (BE връща един и същ 404 и за „няма такъв ресторант").
      if ((await probeTerminalToken()) === "dead") fullLogout();
    } finally {
      setLoading(false);
    }
  };

  return <CodeKeypad onCodeSubmit={handleCodeSubmit} loading={loading} />;
}
